import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, FileText, Download } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";

const STEPS = ["pending", "assigned", "in_progress", "completed"];

export default function PortalWorkOrderDetail() {
  const { id } = useParams();
  const [wo, setWo] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/work-orders/${id}`).then((r) => setWo(r.data)).catch((e) => setError("Unable to load work order."));
  }, [id]);

  if (error) return <div className="text-red-700">{error}</div>;
  if (!wo) return <div className="text-slate-400">Loading…</div>;

  const currentStep = STEPS.indexOf(wo.status);
  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="space-y-8" data-testid="wo-detail">
      <Link to="/portal/work-orders" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to work orders
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-mono-alt">WO-{wo.id.slice(-6).toUpperCase()}</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tighter font-medium mt-2">{wo.property_address}</h1>
          <div className="mt-2 text-sm text-slate-500">{wo.service_type} · Priority: <span className="capitalize">{wo.priority}</span></div>
        </div>
        <StatusBadge status={wo.status} />
      </div>

      {/* Pipeline steps */}
      <div className="bg-white border border-slate-200 p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-4">Pipeline</div>
        <div className="grid grid-cols-4 gap-2">
          {STEPS.map((s, i) => {
            const active = i <= currentStep && wo.status !== "cancelled";
            return (
              <div key={s} className="flex flex-col gap-2">
                <div className={`h-1.5 ${active ? "bg-[#1E40AF]" : "bg-slate-200"}`} />
                <div className={`text-xs font-medium capitalize ${active ? "text-[#0B192C]" : "text-slate-400"}`}>
                  {s.replace("_", " ")}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <Card title="Notes from client">
            {wo.notes ? <p className="text-sm text-slate-700 leading-relaxed">{wo.notes}</p> : <div className="text-sm text-slate-400">No notes provided.</div>}
          </Card>

          <Card title="Admin notes">
            {wo.admin_notes ? <p className="text-sm text-slate-700 leading-relaxed">{wo.admin_notes}</p> : <div className="text-sm text-slate-400">No updates yet.</div>}
          </Card>

          <Card title={`Photos (${wo.photos?.length || 0})`}>
            {!wo.photos?.length && <div className="text-sm text-slate-400">No photos uploaded yet.</div>}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
              {wo.photos?.map((p) => (
                <a key={p.id} href={`${backend}${p.url}`} target="_blank" rel="noreferrer" className="group block border border-slate-200 overflow-hidden aspect-square">
                  <img src={`${backend}${p.url}`} alt={p.caption} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
                </a>
              ))}
            </div>
          </Card>

          <Card title={`Reports (${wo.reports?.length || 0})`}>
            {!wo.reports?.length && <div className="text-sm text-slate-400">No reports uploaded yet.</div>}
            <div className="divide-y divide-slate-100">
              {wo.reports?.map((r) => (
                <a key={r.id} href={`${backend}${r.url}`} target="_blank" rel="noreferrer" className="flex items-center justify-between py-3 hover:text-[#1E40AF]">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="h-5 w-5 text-slate-500 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{r.title}</div>
                      <div className="text-xs text-slate-500">{new Date(r.uploaded_at).toLocaleString()}</div>
                    </div>
                  </div>
                  <Download className="h-4 w-4" />
                </a>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card title="Details">
            <dl className="text-sm divide-y divide-slate-100">
              <Row k="Client" v={wo.client_company || wo.client_name} />
              <Row k="Requested date" v={wo.requested_date || "—"} />
              <Row k="Assigned to" v={wo.assigned_to || "Unassigned"} />
              <Row k="Created" v={new Date(wo.created_at).toLocaleString()} />
              <Row k="Updated" v={new Date(wo.updated_at).toLocaleString()} />
            </dl>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white border border-slate-200">
      <div className="px-5 py-3 border-b border-slate-200 text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{title}</div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="py-2 flex items-baseline justify-between gap-4">
      <dt className="text-[11px] uppercase tracking-[0.14em] text-slate-500">{k}</dt>
      <dd className="text-slate-800 text-right">{v}</dd>
    </div>
  );
}
